import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { AlertTriangle } from 'lucide-react';
import { Building } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import { translate } from '../../../types/EnumTranslations';
import { componentFieldsApi } from '../../../api/componentFieldsApi';
import { BuildingCheckMap, componentDeletionEffects } from '../../../Helpers/componentDeletion';
import { buildingChecksApi } from '../../../api/buildingChecksApi';
import { resolveComponentLocation } from '../../../Helpers/ComponentRoomUtils';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { ValidationPhotos } from './ValidationPhotos';
import './DeleteComponentModal.css';

const MAX_LISTED_DEPENDENTS = 5;

/**
 * Confirms and performs the deletion of one component. States exactly which
 * component goes, what goes with it (photos, locks, its checks) and which
 * components are left without their surface.
 */
export const DeleteComponentModal: React.FC<{
    component: BaseComponentImpl;
    building: Building;
    buildingKey: string;
    onDeleted: (componentKey: string) => void;
    onClose: () => void;
}> = ({ component, building, buildingKey, onDeleted, onClose }) => {
    const { types: buildComponentTypes } = useBuildComponentTypes();
    const typeName = buildComponentTypes[component.specificType?.value || '']?.translations.sv || null;
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const location = resolveComponentLocation(component, building.model.structure);
    const placement = [location.levelDisplay, location.roomDisplay].filter(Boolean).join(', ');
    const photoCount = Object.keys(component.photos || {}).length;

    // The building's checks, only to say how many go: the server trigger removes those about
    // the component (or its locks). If the load fails the line is left out.
    const [checks, setChecks] = useState<BuildingCheckMap>({});
    useEffect(() => {
        let cancelled = false;
        buildingChecksApi.loadBuildingCheckInstances(buildingKey)
            .then(loaded => { if (!cancelled) setChecks(loaded); })
            .catch(err => console.error('Error loading checks:', err));
        return () => { cancelled = true; };
    }, [buildingKey]);

    const { lockCount, dependentNames, checkCount } = useMemo(() => {
        const components = building.model.structure?.components || {};
        const { lockKeys, detachedKeys, checkKeys } = componentDeletionEffects(components, component.key, checks);
        return {
            lockCount: lockKeys.length,
            dependentNames: detachedKeys.map(key => new BaseComponentImpl(components[key]).displayName(building)),
            checkCount: checkKeys.length,
        };
    }, [building, component.key, checks]);

    const handleDelete = async () => {
        setDeleting(true);
        setError(null);
        try {
            await componentFieldsApi.deleteComponent(buildingKey, component.key);
            onDeleted(component.key);
        } catch (err) {
            console.error('Error deleting component:', err);
            setError(err instanceof Error ? err.message : 'Kunde inte ta bort komponenten. Försök igen.');
            setDeleting(false);
        }
    };

    return (
        <Modal show onHide={deleting ? undefined : onClose} centered>
            <Modal.Header closeButton={!deleting}>
                <Modal.Title>Ta bort komponent</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Du är på väg att ta bort den här komponenten från {building.summary?.name || 'byggnaden'}:</p>

                <div className="delete-component-card">
                    <div className="delete-component-name">{component.displayName(building)}</div>
                    <dl className="delete-component-facts">
                        <dt>Komponenttyp</dt>
                        <dd>{[translate(component.type), typeName].filter(Boolean).join(' · ')}</dd>
                        <dt>Placering</dt>
                        <dd>{placement || '–'}</dd>
                        <dt>Foton</dt>
                        <dd>{photoCount === 0 ? 'Inga foton' : `${photoCount} st`}</dd>
                    </dl>
                    {photoCount > 0 && (
                        <div className="delete-component-photos">
                            <ValidationPhotos photos={component.photos} buildingKey={buildingKey} static />
                        </div>
                    )}
                </div>

                {(photoCount > 0 || lockCount > 0 || dependentNames.length > 0 || checkCount > 0) && (
                    <ul className="delete-component-consequences">
                        {photoCount > 0 && (
                            <li>
                                {photoCount === 1 ? 'Komponentens foto' : `Komponentens ${photoCount} foton`} försvinner
                                tillsammans med komponenten.
                            </li>
                        )}
                        {lockCount > 0 && (
                            <li>
                                {lockCount === 1 ? 'Låset' : `De ${lockCount} låsen`} på komponenten tas bort
                                tillsammans med den.
                            </li>
                        )}
                        {checkCount > 0 && (
                            <li>
                                {checkCount === 1 ? 'Komponentens kontroll' : `Komponentens ${checkCount} kontroller`} tas
                                bort. Utförda kontroller sparas.
                            </li>
                        )}
                        {dependentNames.length > 0 && (
                            <li>
                                {dependentNames.length === 1
                                    ? '1 komponent är placerad på den här och blir utan yta: '
                                    : `${dependentNames.length} komponenter är placerade på den här och blir utan yta: `}
                                {dependentNames.slice(0, MAX_LISTED_DEPENDENTS).join(', ')}
                                {dependentNames.length > MAX_LISTED_DEPENDENTS &&
                                    ` och ${dependentNames.length - MAX_LISTED_DEPENDENTS} till`}
                                . De tas inte bort.
                            </li>
                        )}
                    </ul>
                )}

                <p className="delete-component-warning mb-0">
                    <AlertTriangle size={16} aria-hidden="true" /> Detta går inte att ångra.
                </p>

                {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={deleting}>
                    Avbryt
                </Button>
                <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Tar bort...' : 'Ta bort komponent'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
